import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useWorkspaceOrganisations, fetchConsolidationOrganisationInfo } from '@futrli/organisations';
import { Consolidation, ConsolidationOrganisationItem, OrganisationConfigItem } from '@futrli-sdk/consolidations';
import { Organisation } from '@futrli-sdk/organisations';
import { usePromiseState } from '@futrli/shared-services';
import { toastError, toastDismiss, ToastId } from '@futrli/ui-components';

export interface ConsolidationEntity extends Organisation, OrganisationConfigItem {
  fullName: string;
  short_code?: string;
}

/**
 * Takes ana array of organisations, organisationConfigData and an optional ConsolidationOrganisation
 * array and returns an array of merged data
 * @param organisations - List of organisations
 * @param extraOrgsData - List of extra data for the given organisations
 * @param consolidationOrgs - Optional list of consolidation organisations. Used for `code`
 */
export const mergeOrganisationData = (
  entities: ConsolidationOrganisationItem[],
  organisations: Organisation[],
  extraOrgsData: OrganisationConfigItem[]
) => {
  const output: ConsolidationEntity[] = organisations.map(org => {
    const entity = entities.find(ent => ent.organisation_id === org.id);
    const extra = extraOrgsData.find(ex => ex.id === org.id) || ({} as OrganisationConfigItem);

    return {
      ...org,
      ...extra,
      name: extra.name || org.name,
      fullName: `${extra.name || org.name} (${entity?.short_code || '???'})`,
      short_code: entity?.short_code
    };
  });

  return output;
};

/**
 * Returns an array of consolidation entities (organisations with extra entity data merged in)
 * for the given consolidation. The fact that we have to build this data from three different
 * sources is quite horrible (and the reason why this hook exists).
 */
export const useConsolidationEntities = (consolidation?: Consolidation) => {
  const { workspaceId: tempWorkspaceId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const { organisations: workspaceOrgs, isDataLoading: workspaceOrgsLoading } = useWorkspaceOrganisations(workspaceId);

  const [entitiesReq, setEntitiesReq] = usePromiseState<ConsolidationEntity[]>();
  const errorToast = useRef<ToastId>();

  // If workspace organisations change, load extra data and merge
  useEffect(() => {
    if (!workspaceOrgsLoading && consolidation) {
      const fetchData = async () => {
        const extraOrgsData = await fetchConsolidationOrganisationInfo(workspaceId);
        const mergedOrgsData = mergeOrganisationData(consolidation.organisations, workspaceOrgs, extraOrgsData);
        return mergedOrgsData.filter(org =>
          (consolidation.organisations || []).find(o => o.organisation_id === org.id)
        );
      };

      const loadData = async () => {
        if (consolidation.organisations) {
          try {
            await setEntitiesReq(fetchData());
          } catch (e) {
            errorToast.current = toastError(
              <FormattedMessage
                id="consolidation.failed_to_load_organisations"
                defaultMessage="Failed to load organisation data"
              />
            );
          }
        }
      };

      loadData();
    }
  }, [consolidation, setEntitiesReq, workspaceId, workspaceOrgs, workspaceOrgsLoading]);

  // Close toast on unmount if shown
  useEffect(
    () => () => {
      if (errorToast.current) {
        toastDismiss(errorToast.current);
      }
    },
    []
  );

  return entitiesReq;
};
